import React, { useMemo, useState } from 'react';
import { X, Check } from 'lucide-react';
import type { BillingCycle, BillingPlan } from '../../lib/billingApi';
import { PRO_ANNUAL_SAVINGS_PCT } from '../../config/proPricing';

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(0)}`;
}

function annualSavings(monthlyCents: number, annualCents: number) {
  const yearlyMonthly = monthlyCents * 12;
  return Math.max(yearlyMonthly - annualCents, 0);
}

export function PlanComparisonModal(props: {
  isOpen: boolean;
  onClose: () => void;
  plans: BillingPlan[];
  currentTier: string;
  onSelect: (tier: BillingPlan['tier'], billingCycle: BillingCycle) => void;
}) {
  const { isOpen, onClose, plans, currentTier, onSelect } = props;
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  if (!isOpen) return null;

  const ordered = useMemo(() => {
    const order = ['free', 'pro'] as const;
    const map = new Map(plans.map((p) => [p.tier, p]));
    return order.map((t) => map.get(t)).filter(Boolean) as BillingPlan[];
  }, [plans]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-5xl bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-black">Compare plans</h2>
            <p className="text-sm text-gray-600 mt-1">Only comment summaries are limited. All other features are unlimited.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-center mb-6">
            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  billingCycle === 'monthly' ? 'bg-white text-black shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  billingCycle === 'annual' ? 'bg-white text-black shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annual <span className="ml-1 text-xs text-green-700 font-semibold">Save {PRO_ANNUAL_SAVINGS_PCT}%</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {ordered.map((p) => {
              const isCurrent = (currentTier || 'free') === p.tier;
              const monthly = p.price_monthly_cents;
              const annual = p.price_annual_cents;
              const displayPrice = billingCycle === 'annual' ? Math.round(annual / 12) : monthly;
              const savings = annualSavings(monthly, annual);

              return (
                <div
                  key={p.tier}
                  className={`border rounded-xl p-4 ${isCurrent ? 'border-green-400 bg-green-50/40' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-bold text-black">{p.name}</div>
                    {isCurrent && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-800 bg-green-100 px-2 py-1 rounded-full">
                        <Check className="w-3.5 h-3.5" /> Current
                      </span>
                    )}
                  </div>

                  <div className="mt-3">
                    <div className="text-3xl font-bold text-black">{formatMoney(displayPrice)}</div>
                    <div className="text-xs text-gray-600">
                      /month{billingCycle === 'annual' ? ` (billed annually ${formatMoney(annual)}/yr)` : ''}
                    </div>
                    {billingCycle === 'annual' && savings > 0 && (
                      <div className="text-xs text-green-700 font-semibold mt-1">Save {formatMoney(savings)}/year</div>
                    )}
                  </div>

                  <div className="mt-4 text-sm text-gray-700">
                    <div className="font-semibold text-gray-900">Comment summaries</div>
                    <div className="mt-1">
                      {p.comment_summaries_limit === null ? 'Unlimited' : `${p.comment_summaries_limit.toLocaleString()}/billing period`}
                    </div>
                  </div>

                  <button
                    disabled={p.tier === 'free' || isCurrent}
                    onClick={() => onSelect(p.tier, billingCycle)}
                    className={`mt-5 w-full px-4 py-2 rounded-lg font-semibold ${
                      p.tier === 'free' || isCurrent
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {isCurrent ? 'Current plan' : p.tier === 'free' ? 'Free' : 'Select plan'}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-xs text-gray-600">
            Billing starts immediately on upgrade. Your billing anniversary date stays the same.
          </div>
        </div>
      </div>
    </div>
  );
}


